import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography, Dialog, DialogTitle, DialogContent, List, ListItemButton, ListItemAvatar, ListItemText, Avatar, Chip, IconButton, InputAdornment, TextField } from '@mui/material';
import { Close as CloseIcon, Person as PersonIcon, AdminPanelSettings, LocalHospital, MedicalServices, SupervisedUserCircle, Science, Biotech, Search as SearchIcon } from '@mui/icons-material';
import { AuthProvider, useAuth } from 'react-oidc-context';
// KEYCLOAK_MIGRATION: Login.js kept but no longer used as entry point
// import Login from './components/Login';
import Dashboard from './components/Dashboard';
import MTRPopup from './components/MTRPopup';
import MTRDashboard from './components/MTRDashboard';
import Admin from './components/Admin';
import Statistics from './components/Statistics';
import Profile from './components/Profile';
import AppBarComponent from './components/AppBarComponent';
import ThemeSwitcher from './components/ThemeSwitcher';
import Demo from './components/Demo';
import ScheduledActionNotification from './components/ScheduledActionNotification';
import AutoLogoutWarning from './components/AutoLogoutWarning';
import InactivityIndicator from './components/InactivityIndicator';
// Reporting components
import ApprovalWorkflow from './components/reporting/ApprovalWorkflow';
import MyTasks from './components/reporting/MyTasks';
import TeamTasks from './components/reporting/TeamTasks';
import Calendar from './components/reporting/Calendar';
import Search from './components/reporting/Search';
import PDFPreviewWindow from './components/reporting/PDFPreviewWindow';
import AISummaryWindow from './components/reporting/AISummaryWindow';
import ReportSidebarPopup from './components/reporting/editor/ReportSidebarPopup';
import ReportEditor from './components/reporting/ReportEditor';
import AddPatient from './components/AddPatient';
// Central Snackbar
import { SnackbarProvider } from './components/CentralSnackbar';
// Chat
import Chat from './components/Chat';
import { PatientContextProvider } from './contexts/PatientContext';
// WebSocket für Echtzeit-Benachrichtigungen
import { WebSocketProvider } from './contexts/WebSocketContext';

import { useThemeContext } from './theme/CustomThemeProvider';
import { oidcConfig } from './config/keycloak';
import { extractPermissions } from './utils/keycloakUtils';
import { setAuthRef } from './utils/authUtils';
import { loadServerConfig } from './utils/apiConfig';
import sessionService from './utils/sessionService';
import { signalReportSidebarLogout, REPORT_SIDEBAR_WINDOW_NAME } from './utils/reportSidebarSync';

const AppStateContext = React.createContext({});

const DEMO_USERS = [
  { username: 'visusadmin', name: 'VISUS Administrator', role: 'System-Admin', icon: <AdminPanelSettings />, color: 'var(--rd-demo-1)' },
  { username: 'sabinerichter', name: 'Prof. Dr. Sabine Richter', role: 'Chefärztin', icon: <SupervisedUserCircle />, color: 'var(--rd-demo-2)' },
  { username: 'sebastianhoffmann', name: 'PD Dr. Sebastian Hoffmann', role: 'Ltd. Oberarzt', icon: <SupervisedUserCircle />, color: 'var(--rd-demo-3)' },
  { username: 'michaelfischer', name: 'Dr. Michael Fischer', role: 'Oberarzt', icon: <LocalHospital />, color: 'var(--rd-demo-4)' },
  { username: 'miriamkoch', name: 'Dr. Miriam Koch', role: 'Fachärztin', icon: <MedicalServices />, color: 'var(--rd-demo-5)' },
  { username: 'lauraschneider', name: 'Dr. Laura Schneider', role: 'Assistenzärztin', icon: <Science />, color: 'var(--rd-demo-6)' },
  { username: 'steffentietz', name: 'Steffen Tietz', role: 'Leitender MTR', icon: <Biotech />, color: 'var(--rd-demo-7)' },
  { username: 'test', name: 'Test User', role: 'Basis-User', icon: <PersonIcon />, color: 'var(--rd-demo-8)' },
];

function AppContent() {
  const {
    isLoggedIn, permissions, handleLogout, mode,
    chatOpen, setChatOpen, demoDialogOpen, setDemoDialogOpen,
    demoSearch, setDemoSearch, handleDemoLogin,
    showLogoutWarning, setShowLogoutWarning, warningShownRef
  } = React.useContext(AppStateContext);

  const location = useLocation();
  const isPdfPreview = location.pathname === '/pdf-preview';
  const isCockpit = location.pathname === '/radiographer-cockpit';
  const isAiSummaryPopup = location.pathname === '/ai-summary-popup';
  const isReportSidebarPopup = location.pathname === '/report-sidebar-popup';

  if (isPdfPreview) {
    return <Routes><Route path="/pdf-preview" element={<PDFPreviewWindow />} /></Routes>;
  }

  if (isCockpit) {
    return <Routes><Route path="/radiographer-cockpit" element={<MTRPopup />} /></Routes>;
  }

  if (isAiSummaryPopup) {
    return <Routes><Route path="/ai-summary-popup" element={<AISummaryWindow />} /></Routes>;
  }

  if (isReportSidebarPopup) {
    return (
      <SnackbarProvider>
        <Routes><Route path="/report-sidebar-popup" element={<ReportSidebarPopup />} /></Routes>
      </SnackbarProvider>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      <AppBarComponent
        isLoggedIn={isLoggedIn}
        {...permissions}
        onLogout={handleLogout}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          // Nur der Content scrollt; AppBar und Tab-Leisten bleiben stehen
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          padding: '20px 0',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          '& .login-container': {
            padding: 0, alignItems: 'center', height: '100vh',
            marginTop: '-20px',
          },
          '& .login-page': {
            padding: 0, margin: 0, height: '100vh', width: '100vw',
            position: 'fixed', top: 0, left: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }
        }}
      >
        <Routes>
          <Route path="/login" element={<Navigate to="/" />} />
          <Route path="/callback" element={<Navigate to="/" />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/demo" element={<Demo />} />
          <Route path="/admin" element={(permissions.isAdmin || permissions.canManageReporting) ? <Admin isAdmin={permissions.isAdmin} canManageReporting={permissions.canManageReporting} /> : <Navigate to="/" />} />
          <Route path="/statistics" element={permissions.canViewStats ? <Statistics /> : <Navigate to="/" />} />
          <Route path="/approval" element={permissions.canApprove ? <ApprovalWorkflow /> : <Navigate to="/" />} />
          <Route path="/my-tasks" element={<MyTasks />} />
          <Route path="/team-tasks" element={<TeamTasks />} />
          <Route path="/calendar" element={permissions.canViewCalendar ? <Calendar /> : <Navigate to="/" />} />
          <Route path="/search" element={<Search />} />
          <Route path="/add-patient" element={<AddPatient />} />
          <Route path="/report/combined" element={<ReportEditor />} />
          <Route path="/report/combined/:reportId" element={<ReportEditor />} />
          <Route path="/report/:examId" element={<ReportEditor />} />
          <Route path="/mtr" element={permissions.isMTR ? <MTRDashboard onLogout={handleLogout} /> : <Navigate to="/" />} />
          <Route path="/" element={permissions.isMTR ? <Navigate to="/mtr" /> : <Dashboard />} />
        </Routes>
      </Box>

      <InactivityIndicator isLoggedIn={isLoggedIn} />

      <AutoLogoutWarning
        open={showLogoutWarning}
        onCancel={() => {
          setShowLogoutWarning(false);
          warningShownRef.current = false;
          sessionStorage.setItem('lastActivity', Date.now().toString());
        }}
        onLogout={handleLogout}
      />

      <ScheduledActionNotification />

      <Chat open={chatOpen} onClose={() => setChatOpen(false)} />

      <Dialog
        open={demoDialogOpen}
        onClose={() => setDemoDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'linear-gradient(135deg, var(--rd-bg-default) 0%, var(--rd-bg-subtle) 100%)',
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>Demo Login</Typography>
            <Chip label="Ctrl+D" size="small" variant="outlined" sx={{ ml: 1, fontSize: '0.7rem' }} />
          </Box>
          <IconButton onClick={() => setDemoDialogOpen(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 2, pb: 2 }}>
          <TextField
            autoFocus fullWidth size="small" placeholder="User suchen..."
            value={demoSearch}
            onChange={(e) => setDemoSearch(e.target.value)}
            sx={{ mb: 1.5 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Passwort: <b>Test123</b>
          </Typography>
          <List sx={{ pt: 0 }}>
            {DEMO_USERS
              .filter(u => !demoSearch || u.name.toLowerCase().includes(demoSearch.toLowerCase()) || u.role.toLowerCase().includes(demoSearch.toLowerCase()))
              .map((user) => (
              <ListItemButton
                key={user.username}
                onClick={() => handleDemoLogin(user.username)}
                sx={{ borderRadius: 2, mb: 0.5, '&:hover': { backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)' } }}
              >
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: user.color, width: 40, height: 40 }}>{user.icon}</Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={user.name} secondary={user.role}
                  primaryTypographyProps={{ fontWeight: 500, fontSize: '0.95rem' }}
                  secondaryTypographyProps={{ fontSize: '0.8rem' }}
                />
                <Chip label={user.username} size="small" variant="outlined" sx={{ fontSize: '0.7rem', opacity: 0.7 }} />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

function AppInner() {
  const auth = useAuth();
  const isLoggedIn = auth.isAuthenticated;
  const [permissions, setPermissions] = useState({
    isAdmin: false,
    canManageReporting: false,
    canViewStats: false,
    canApprove: false,
    canVerify: false,
    canViewCalendar: false,
    canEditCalendar: false,
    canViewPersonalStats: false,
    canViewTeamStats: false,
    canViewModalityStats: false,
    canViewStudyStats: false,
    canDownloadPersonalStats: false,
    canDownloadTeamStats: false,
    canDownloadModalityStats: false,
    canDownloadStudyStats: false,
    usesDarkmode: false,
    isMTR: false
  });
  const { mode, setInitialTheme } = useThemeContext();
  const [chatOpen, setChatOpen] = useState(false);
  const [demoDialogOpen, setDemoDialogOpen] = useState(false);
  const [demoSearch, setDemoSearch] = useState('');
  const [showLogoutWarning, setShowLogoutWarning] = useState(false);
  const [autoLogoutMinutes, setAutoLogoutMinutes] = useState(10);
  const warningShownRef = useRef(false);
  const lastUserSubRef = useRef(null);

  // Synchronously set auth ref so child useEffects already have the token
  setAuthRef(auth);

  useEffect(() => {
    document.body.className = mode;
  }, [mode]);

  // Chat-Event-Listener
  useEffect(() => {
    const handleOpenChat = () => {
      setChatOpen(true);
    };
    window.addEventListener('openChat', handleOpenChat);
    return () => {
      window.removeEventListener('openChat', handleOpenChat);
    };
  }, []);

  // Ctrl+D: Demo Mode – quick-switch between demo users
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === 'd') {
        e.preventDefault();
        setDemoDialogOpen(true);
        setDemoSearch('');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleDemoLogin = useCallback((username) => {
    setDemoDialogOpen(false);
    auth.removeUser().then(() => {
      auth.signinRedirect({ login_hint: username, prompt: 'login' });
    });
  }, [auth]);

  // Extract permissions and load user data — only on actual user change, not on token refresh
  useEffect(() => {
    if (!auth.isAuthenticated || !auth.user) {
      lastUserSubRef.current = null;
      return;
    }

    const currentSub = auth.user.profile?.sub;
    if (currentSub && currentSub === lastUserSubRef.current) return;
    lastUserSubRef.current = currentSub;

    const perms = extractPermissions(auth);
    setPermissions(perms);

    const loadUserData = async () => {
      try {
        const resp = await fetch(
          (process.env.REACT_APP_API_URL || `${window.location.protocol}//${window.location.hostname}:25000`) + '/auth/me',
          { headers: { Authorization: `Bearer ${auth.user.access_token}` } }
        );
        if (resp.ok) {
          const data = await resp.json();
          sessionService.setCurrentUser(data);
          // App-Konfiguration (z.B. password_for_report_approval) aus /auth/me
          // im sessionStorage ablegen, damit sessionService.getConfig() sie liefert.
          // Ohne dies greift der Keycloak-Flow nie auf die config.ini-Settings zu.
          if (data.config) {
            sessionStorage.setItem('app_config', JSON.stringify(data.config));
          }
          if (data.uses_darkmode) {
            setPermissions(prev => ({ ...prev, usesDarkmode: true }));
            setInitialTheme('dark');
          }
        }
      } catch (e) {
        console.warn('Could not load user data:', e);
      }
    };
    loadUserData();

    loadServerConfig().catch(e => console.warn('Could not load server config:', e));
  }, [auth.isAuthenticated, auth.user]);

  // Activity tracking + auto-logout check
  useEffect(() => {
    if (!auth.isAuthenticated) return;

    // Load autologout config from backend
    const loadAutoLogoutConfig = async () => {
      try {
        const apiBase = process.env.REACT_APP_API_URL || `${window.location.protocol}//${window.location.hostname}:25000`;
        const resp = await fetch(`${apiBase}/config/autologout`);
        if (resp.ok) {
          const data = await resp.json();
          if (data.success && data.autologout_minutes) {
            setAutoLogoutMinutes(data.autologout_minutes);
          }
        }
      } catch (e) {
        // Use default
      }
    };
    loadAutoLogoutConfig();

    // Set initial activity timestamp
    sessionStorage.setItem('lastActivity', Date.now().toString());

    // Track user activity
    const trackActivity = () => {
      sessionStorage.setItem('lastActivity', Date.now().toString());
      if (warningShownRef.current) {
        warningShownRef.current = false;
        setShowLogoutWarning(false);
      }
    };

    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    activityEvents.forEach(evt => window.addEventListener(evt, trackActivity, { passive: true }));

    // Check inactivity every 30 seconds
    const checkInterval = setInterval(() => {
      const lastActivity = sessionStorage.getItem('lastActivity');
      if (!lastActivity) return;
      const inactiveMinutes = (Date.now() - parseInt(lastActivity)) / (1000 * 60);
      if (inactiveMinutes >= autoLogoutMinutes && !warningShownRef.current) {
        warningShownRef.current = true;
        setShowLogoutWarning(true);
      }
    }, 30000);

    return () => {
      activityEvents.forEach(evt => window.removeEventListener(evt, trackActivity));
      clearInterval(checkInterval);
    };
  }, [auth.isAuthenticated, autoLogoutMinutes]);

  const handleLogout = useCallback(() => {
    signalReportSidebarLogout();
    try {
      const sidebarPopup = window.open('', REPORT_SIDEBAR_WINDOW_NAME);
      if (sidebarPopup && !sidebarPopup.closed) {
        sidebarPopup.close();
      }
    } catch (_) {
      // ignore popup access errors
    }
    // Signal an MTR-Popup senden, damit es sich schließt
    if (permissions.isMTR) {
      const logoutEvent = { type: 'logout', timestamp: Date.now() };
      localStorage.setItem('mtr-logout-signal', JSON.stringify(logoutEvent));
      window.dispatchEvent(new StorageEvent('storage', {
        key: 'mtr-logout-signal',
        newValue: JSON.stringify(logoutEvent),
        url: window.location.href
      }));
    }
    auth.signoutRedirect();
  }, [auth, permissions.isMTR]);

  // OIDC loading / redirect states
  if (auth.isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          gap: 2
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Anwendung wird initialisiert...
        </Typography>
      </Box>
    );
  }

  // Auth error (e.g. invalid scope, misconfiguration) – show message instead of redirect loop
  if (auth.error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          gap: 2,
          p: 4,
          textAlign: 'center'
        }}
      >
        <Typography variant="h5" color="error">
          Authentifizierungsfehler
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600 }}>
          {auth.error.message || String(auth.error)}
        </Typography>
        <Typography
          variant="body2"
          sx={{ cursor: 'pointer', color: 'primary.main', mt: 2, textDecoration: 'underline' }}
          onClick={() => auth.signinRedirect()}
        >
          Erneut versuchen
        </Typography>
      </Box>
    );
  }

  // Not authenticated: redirect to Keycloak
  if (!isLoggedIn) {
    auth.signinRedirect();
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          gap: 2
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Weiterleitung zum Login...
        </Typography>
      </Box>
    );
  }

  const appState = {
    isLoggedIn, permissions, handleLogout, mode,
    chatOpen, setChatOpen, demoDialogOpen, setDemoDialogOpen,
    demoSearch, setDemoSearch, handleDemoLogin,
    showLogoutWarning, setShowLogoutWarning, warningShownRef
  };

  return (
    <AppStateContext.Provider value={appState}>
      <SnackbarProvider>
        <PatientContextProvider>
          <WebSocketProvider>
            <Router>
              <AppContent />
            </Router>
          </WebSocketProvider>
        </PatientContextProvider>
      </SnackbarProvider>
    </AppStateContext.Provider>
  );
}

function App() {
  return (
    <AuthProvider {...oidcConfig}>
      <AppInner />
    </AuthProvider>
  );
}

export default App;
